import Logo from '@/components/logo';
import Title from '@/components/title';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { orderProducts, getMe, updatePartnerData } from '@/lib/api';
import { useCartStore } from '@/store/cart';
import type { OrderItem, Product } from '@/types/index';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { Minus, Plus, Trash } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useEffect } from 'react';

const formSchema = z.object({
  firstName: z.string().min(1, {
    message: 'Vezetéknév megadása szükséges.',
  }),
  lastName: z.string().min(1, {
    message: 'Keresztnév megadása szükséges.',
  }),
  email: z.string().email({
    message: 'Hibás email cím.',
  }),
  phone: z.string().min(1, {
    message: 'Telefonszám megadása szükséges.',
  }),
  partnerName: z.string().min(1, {
    message: 'Partner neve megadása szükséges.',
  }),
  partnerAddress: z.string().min(1, {
    message: 'Partner címe megadása szükséges.',
  }),
  semId: z.string().min(1, {
    message: 'SemId megadása szükséges.',
  }),
});

export default function Footer({
  productList,
}: {
  productList: Product[] | null;
}) {
  const {
    products,
    removeProduct,
    getTotalCartCredits,
    addProduct,
    availableCredits,
    resetCart,
    updateAvailableCredits,
    updateQuantity,
  } = useCartStore();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    const initializeForm = async () => {
      const userData = await getMe();
      if (userData?.partnerDatum) {
        const { partnerDatum } = userData;
        form.reset({
          firstName: partnerDatum.firstName,
          lastName: partnerDatum.lastName,
          email: userData.email,
          phone: partnerDatum.phone,
          partnerName: partnerDatum.partnerName,
          partnerAddress: partnerDatum.partnerAddress,
          semId: partnerDatum.semId,
        });
      }
    };

    initializeForm();
  }, [form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const partnerDataResponse = await updatePartnerData({
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      partnerName: values.partnerName,
      partnerAddress: values.partnerAddress,
    });

    if (!partnerDataResponse) {
      toast.error('A partner adatok frissítése sikertelen!');
      return;
    }

    try {
      const orderItems: OrderItem[] = products.map((product) => ({
        productId: product.id,
        quantity: product.quantity,
      }));

      await orderProducts(orderItems);

      // Get updated user data after order, forcing a refresh from the server
      const userData = await getMe({ forceRefresh: true });
      if (
        userData &&
        userData.partnerDatum &&
        typeof userData.partnerDatum.totalCredit === 'number' &&
        typeof userData.partnerDatum.usedCredit === 'number'
      ) {
        // Calculate available credits from the updated data
        const available =
          userData.partnerDatum.totalCredit +
          (userData.partnerDatum.extraCredit || 0) -
          userData.partnerDatum.usedCredit;
        updateAvailableCredits(available);
      }

      // Clear cart from localStorage and reset store
      resetCart();
      toast.success('Pontbeváltás sikeres!');
    } catch (error) {
      console.error('Order failed:', error);
      toast.error('A pontbeváltás sikertelen! Kérjük próbáld újra.');
    }
  }

  return (
    <footer className=" text-white bg-gradient-to-r from-heineken-dark-green via-heineken-light-green to-heineken-green py-24 px-4 md:px-0">
      <div className="container mx-auto ">
        <h1 className="text-4xl xl:text-5xl font-bold mb-10 xl:mb-20 text-center font-heineken text-white">
          Kreditbeváltás
        </h1>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col-reverse md:flex-row justify-between gap-10 md:gap-20"
          >
            <div className="flex flex-col gap-4 flex-1">
              <Title text="Adatellenőrzés" colorClass="text-white" isSmall />
              <p className="mb-10">
                A regisztráció során megadott adataid. (Szerkezd a mezőket, ha
                valami nem megfelelő)
              </p>

              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Vezetéknév</FormLabel>
                    <FormControl>
                      <Input placeholder="Kovács" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Keresztnév</FormLabel>
                    <FormControl>
                      <Input placeholder="János" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Email cím</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="pelda@email.hu"
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Telefonszám</FormLabel>
                    <FormControl>
                      <Input placeholder="+36 20 123 4567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partnerName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Partner neve</FormLabel>
                    <FormControl>
                      <Input placeholder="Partner neve" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partnerAddress"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Partner címe</FormLabel>
                    <FormControl>
                      <Input placeholder="Partner címe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="semId"
                render={({ field }) => (
                  <FormItem className="w-full ">
                    <FormLabel>SemId</FormLabel>
                    <FormControl>
                      <Input placeholder="1234567890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col justify-start">
              <Title
                text="Itt válassz az ajándékok közül"
                colorClass="text-white"
                isSmall
              />

              <div className="mb-6">
                <p className="text-sm font-medium mb-1">Válassz ajándékot</p>
                <Select
                  onValueChange={(productId) => {
                    const productToAdd = productList?.find(
                      (p: Product) => p.id === productId
                    );
                    if (productToAdd) {
                      addProduct(productToAdd);
                    }
                  }}
                  value=""
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Válassz terméket..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Elérhető termékek</SelectLabel>
                      {productList?.length ? (
                        productList.map((item: Product) => {
                          const cartItem = products.find(
                            (p) => p.id === item.id
                          );
                          const totalCartCredits = getTotalCartCredits();
                          const hasEnoughCredits =
                            availableCredits >= totalCartCredits + item.price;

                          return (
                            <SelectItem
                              key={item.id}
                              value={item.id}
                              disabled={!hasEnoughCredits}
                            >
                              {item.name} ({item.price} kr)
                              {cartItem && ` - ${cartItem.quantity}x kosárban`}
                              {!hasEnoughCredits && ' - Nincs elég kredit'}
                            </SelectItem>
                          );
                        })
                      ) : (
                        <SelectItem value="no-products" disabled>
                          Nincsenek elérhető termékek
                        </SelectItem>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col flex-1 justify-between">
                {products.length === 0 && (
                  <p className="text-gray-300 mb-10 text-center py-6">
                    Még nem választottál terméket.
                  </p>
                )}

                {products.length > 0 && (
                  <ul className="mb-10 flex-1 overflow-y-auto max-h-64 pr-2">
                    {products.map((product) => (
                      <li
                        key={product.id}
                        className="flex flex-row items-center justify-between gap-2 font-bold bg-white/5 p-2 px-4 rounded-sm mb-2"
                      >
                        <p className="truncate flex-1">
                          {product.name} ({product.price} kr)
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center bg-white/10 rounded-md">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                updateQuantity(product.id, product.quantity - 1)
                              }
                              className="h-8 w-8 hover:bg-white/5"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center">
                              {product.quantity}
                            </span>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                updateQuantity(product.id, product.quantity + 1)
                              }
                              className="h-8 w-8 hover:bg-white/5"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="hover:!bg-red-500/20 cursor-pointer shrink-0"
                            onClick={() => {
                              removeProduct(product.id);
                            }}
                            aria-label={`Remove ${product.name} from cart`}
                            tabIndex={0}
                          >
                            <Trash className="text-red-600 size-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {products.length > 0 && (
                  <div className="mt-auto pt-4 border-t border-white/10">
                    <p className="text-right font-bold text-lg">
                      Összesen: {getTotalCartCredits()} kredit
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-end mt-4">
                <Button
                  type="submit"
                  variant="light"
                  disabled={
                    form.formState.isSubmitting ||
                    !form.formState.isValid ||
                    products.length === 0
                  }
                  className="w-full"
                >
                  Pontbeváltás véglegesítése
                </Button>
              </div>
            </div>
          </form>
        </Form>

        <div className="flex flex-col items-center mt-10 md:mt-24">
          <Logo />

          <p className="text-base text-white uppercase mt-4">
            HA KÉRDÉSED VAN, ÍRJ NEKÜNK A{' '}
            <a href="mailto:liga@solidagency.hu" className="hover:underline">
              LIGA@SOLIDAGENCY.HU
            </a>{' '}
            E-MAIL CÍMRE!
          </p>

          <a
            href="https://www.heineken.com/hu/hu/fogyaszd-felelosseggel"
            className="bg-white text-heineken-green font-bold mx-auto mt-4 px-4 py-2 cursor-pointer"
            target="_blank"
          >
            Fogyaszd felelősséggel
          </a>

          <nav className="mb-6 md:mt-10">
            <ul className="flex flex-row gap-2 text-xs">
              <li>
                <Link to="/privacy" className="hover:underline">
                  Adatvédelmi Tájékoztató
                </Link>
              </li>
              <li>
                <Link to="/rules" className="hover:underline">
                  Játékszabály
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}
